import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MessageSquare, Send, User, RefreshCw, Plus, X } from "lucide-react";

/**
 * ProviderMessagesPage — provider/admin view of the patient ↔ provider
 * messaging thread. Sidebar lists every conversation partner ordered by most
 * recent activity; selecting one opens the full thread on the right with a
 * reply composer. Uses the existing /messages endpoint (already supports
 * `?with_user=...` filtering).
 */
function timeAgo(iso) {
  try {
    const t = (Date.now() - new Date(iso).getTime()) / 1000;
    if (t < 60) return "just now";
    if (t < 3600) return `${Math.floor(t / 60)}m`;
    if (t < 86400) return `${Math.floor(t / 3600)}h`;
    return `${Math.floor(t / 86400)}d`;
  } catch (_) { return ""; }
}

export default function ProviderMessagesPage() {
  const { user } = useAuth();
  const [allMessages, setAllMessages] = useState([]);
  const [partners, setPartners] = useState({}); // id -> {full_name, role, email}
  const [activeId, setActiveId] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [patientList, setPatientList] = useState([]);
  const [newSearch, setNewSearch] = useState("");
  const [newBody, setNewBody] = useState("");
  const bottomRef = useRef(null);

  const loadAll = async () => {
    const m = await api.get("/messages");
    const rows = m.data || [];
    setAllMessages(rows);
    // Collect unique partner ids (the OTHER end of each message)
    const ids = new Set();
    rows.forEach((r) => ids.add(r.sender_id === user.id ? r.recipient_id : r.sender_id));
    const next = { ...partners };
    await Promise.all([...ids].map(async (id) => {
      if (next[id]) return;
      try {
        // Try patient lookup first
        const p = await api.get(`/patients?user_id=${id}`).catch(() => ({ data: [] }));
        const match = (p.data || []).find((x) => x.user_id === id);
        if (match) {
          next[id] = { id, full_name: `${match.first_name} ${match.last_name}`.trim(), role: "patient", email: match.email };
          return;
        }
      } catch (_) {}
      try {
        const r = await api.get("/providers");
        const provider = (r.data || []).find((x) => x.id === id);
        if (provider) next[id] = { id, full_name: provider.full_name, role: provider.role, email: provider.email };
      } catch (_) {}
      if (!next[id]) next[id] = { id, full_name: "Unknown", role: "—", email: "" };
    }));
    setPartners(next);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group messages into conversations keyed by partner id
  const conversations = useMemo(() => {
    const map = {};
    allMessages.forEach((m) => {
      const partnerId = m.sender_id === user.id ? m.recipient_id : m.sender_id;
      if (!map[partnerId]) map[partnerId] = { partnerId, messages: [], unread: 0, lastAt: "" };
      map[partnerId].messages.push(m);
      if (m.recipient_id === user.id && !m.read) map[partnerId].unread += 1;
      if (m.created_at > map[partnerId].lastAt) map[partnerId].lastAt = m.created_at;
    });
    return Object.values(map).sort((a, b) => b.lastAt.localeCompare(a.lastAt));
  }, [allMessages, user.id]);

  // Auto-select first conversation when none picked
  useEffect(() => {
    if (!activeId && conversations.length > 0) setActiveId(conversations[0].partnerId);
  }, [conversations, activeId]);

  const activeThread = conversations.find((c) => c.partnerId === activeId);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [activeThread?.messages?.length]);

  const startNewConversation = async () => {
    setShowNewModal(true);
    if (patientList.length === 0) {
      const r = await api.get("/patients").catch(() => ({ data: [] }));
      setPatientList(r.data || []);
    }
  };
  const sendNewMessage = async (patient) => {
    if (!newBody.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: patient.user_id, body: newBody.trim() });
      setShowNewModal(false); setNewBody(""); setNewSearch("");
      await loadAll();
      setActiveId(patient.user_id);
    } finally { setSending(false); }
  };

  const reply = async (e) => {
    e.preventDefault();
    if (!body.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: activeId, body: body.trim() });
      setBody("");
      await loadAll();
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="provider-messages-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Messages</h1>
          <p className="text-stone-500 mt-1">Patient and team conversations.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadAll} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm" data-testid="messages-refresh">
            <RefreshCw className="w-4 h-4" />Refresh
          </button>
          <button onClick={startNewConversation} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-500 text-white text-sm font-semibold" data-testid="new-conversation-btn">
            <Plus className="w-4 h-4" />Message a patient
          </button>
        </div>
      </header>

      {showNewModal && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-center justify-center p-4" data-testid="new-message-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg border border-stone-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center">
              <h3 className="font-display font-bold text-lg text-stone-900">Send a message</h3>
              <button onClick={() => setShowNewModal(false)} className="p-1.5 rounded-lg hover:bg-stone-100" data-testid="close-new-message"><X className="w-5 h-5 text-stone-500" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <input type="text" value={newSearch} onChange={(e) => setNewSearch(e.target.value)} placeholder="Search patient by name…" className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm" data-testid="new-msg-search" />
              <div className="max-h-48 overflow-y-auto border border-stone-200 rounded-lg divide-y divide-stone-100">
                {patientList
                  .filter((p) => !newSearch || `${p.first_name} ${p.last_name}`.toLowerCase().includes(newSearch.toLowerCase()))
                  .slice(0, 20)
                  .map((p) => (
                    <button key={p.id} onClick={() => sendNewMessage(p)} disabled={!newBody.trim() || sending}
                      className="w-full text-left px-3 py-2 hover:bg-brand-50 text-sm disabled:opacity-50"
                      data-testid={`new-msg-patient-${p.id}`}>
                      <div className="font-semibold text-stone-900">{p.first_name} {p.last_name}</div>
                      <div className="text-xs text-stone-500">{p.email} · MRN {p.mrn}</div>
                    </button>
                  ))}
              </div>
              <textarea rows={3} value={newBody} onChange={(e) => setNewBody(e.target.value)} placeholder="Type your message, then click a patient above to send…"
                className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm resize-none" data-testid="new-msg-body" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-4 bg-white border border-stone-200 rounded-2xl shadow-soft overflow-hidden h-[70vh]">
        {/* Conversation sidebar */}
        <aside className="col-span-12 md:col-span-4 border-r border-stone-100 overflow-y-auto" data-testid="conversation-list">
          {conversations.length === 0 && (
            <div className="p-8 text-center text-stone-500 text-sm">
              <MessageSquare className="w-7 h-7 mx-auto mb-2 text-stone-300" />
              No conversations yet.
            </div>
          )}
          {conversations.map((c) => {
            const p = partners[c.partnerId] || { full_name: "Loading…", role: "" };
            const isActive = activeId === c.partnerId;
            const lastMsg = c.messages[c.messages.length - 1];
            const preview = lastMsg?.sender_id === user.id ? `You: ${lastMsg.body}` : lastMsg?.body;
            return (
              <button
                key={c.partnerId}
                onClick={() => setActiveId(c.partnerId)}
                data-testid={`conversation-${c.partnerId}`}
                className={`w-full text-left px-4 py-3 border-b border-stone-100 transition-colors ${isActive ? "bg-brand-50" : "hover:bg-stone-50"}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${p.role === "patient" ? "bg-rose-100 text-rose-700" : "bg-indigo-100 text-indigo-700"}`}>
                    <User className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-stone-900 truncate">{p.full_name}</span>
                      <span className="text-[10px] text-stone-400 shrink-0">{timeAgo(c.lastAt)}</span>
                    </div>
                    <div className="text-xs text-stone-500 truncate mt-0.5">{p.role}{c.unread > 0 ? ` · ${c.unread} unread` : ""}</div>
                    <div className="text-xs text-stone-600 truncate mt-0.5">{preview}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Active thread */}
        <section className="col-span-12 md:col-span-8 flex flex-col" data-testid="active-thread">
          {!activeThread ? (
            <div className="flex-1 flex items-center justify-center text-stone-500 text-sm">
              Select a conversation to see messages.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-stone-100">
                <div className="font-display font-semibold text-stone-900">{partners[activeThread.partnerId]?.full_name}</div>
                <div className="text-xs text-stone-500">{partners[activeThread.partnerId]?.role} · {partners[activeThread.partnerId]?.email}</div>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2 timeline-scroll" data-testid="thread-messages">
                {activeThread.messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm shadow-soft ${mine ? "bg-brand-500 text-white rounded-br-sm" : "bg-stone-100 text-stone-900 rounded-bl-sm"}`}>
                        <div className="whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-brand-100" : "text-stone-500"}`}>{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={reply} className="border-t border-stone-100 p-3 flex items-end gap-2" data-testid="reply-form">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={`Reply to ${partners[activeThread.partnerId]?.full_name || "patient"}…`}
                  rows={2}
                  className="flex-1 px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:outline-none text-sm resize-none"
                  data-testid="reply-input"
                />
                <button type="submit" disabled={sending || !body.trim()} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 text-white font-semibold text-sm disabled:opacity-50" data-testid="send-reply">
                  <Send className="w-4 h-4" />Send
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
